import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Settings as SettingsIcon, Info, Video, ExternalLink } from "lucide-react";

const API = "/api";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noShowDeduction, setNoShowDeduction] = useState("1");
  const [zoomLink, setZoomLink] = useState("");
  const [classroomMessage, setClassroomMessage] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setNoShowDeduction(response.data.no_show_deduction.toString());
      setZoomLink(response.data.classroom_zoom_link || "");
      setClassroomMessage(response.data.classroom_message || "");
    } catch (error) {
      console.error("Failed to fetch settings:", error);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings`, {
        no_show_deduction: parseInt(noShowDeduction),
        classroom_zoom_link: zoomLink.trim(),
        classroom_message: classroomMessage.trim(),
      });
      setSettings(response.data);
      toast.success("Settings saved successfully.");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Configure class tracking rules</p>
      </div>

      {/* Deduction Rules Card */}
      <Card className="border-slate-200 shadow-sm max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <SettingsIcon className="w-5 h-5 text-indigo-600" />
            Class Deduction Rules
          </CardTitle>
          <CardDescription>
            Configure how classes are deducted based on attendance status
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Fixed Rules */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-slate-700">Fixed Deduction Rules</Label>
            <div className="grid gap-2 text-sm">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
                <span className="font-medium text-green-800">Present</span>
                <span className="text-green-700">-1 class</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                <span className="font-medium text-red-800">Absent</span>
                <span className="text-red-700">No deduction</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                <span className="font-medium text-yellow-800">Cancelled (on time)</span>
                <span className="text-yellow-700">No deduction</span>
              </div>
            </div>
          </div>

          {/* Configurable No Show Rule */}
          <div className="space-y-3 pt-4 border-t border-slate-100">
            <Label htmlFor="noshow" className="text-sm font-medium text-slate-700">
              No Show Deduction
            </Label>
            <div className="flex items-center gap-4">
              <Select value={noShowDeduction} onValueChange={setNoShowDeduction}>
                <SelectTrigger className="w-48" data-testid="noshow-deduction-select">
                  <SelectValue placeholder="Select deduction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No deduction (0)</SelectItem>
                  <SelectItem value="1">Deduct 1 class</SelectItem>
                  <SelectItem value="2">Deduct 2 classes</SelectItem>
                  <SelectItem value="3">Deduct 3 classes</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex-1">
                <div className={`p-3 rounded-lg border ${
                  noShowDeduction === "0" 
                    ? "bg-slate-50 border-slate-200" 
                    : "bg-rose-50 border-rose-100"
                }`}>
                  <span className={`font-medium ${
                    noShowDeduction === "0" ? "text-slate-700" : "text-rose-800"
                  }`}>
                    No Show
                  </span>
                  <span className={`float-right ${
                    noShowDeduction === "0" ? "text-slate-600" : "text-rose-700"
                  }`}>
                    {noShowDeduction === "0" ? "No deduction" : `-${noShowDeduction} class${noShowDeduction !== "1" ? "es" : ""}`}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-500 flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              Changing this setting will recalculate remaining classes for all students.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Classroom / Zoom Link Card */}
      <Card className="border-slate-200 shadow-sm max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Video className="w-5 h-5 text-blue-600" />
            Classroom Settings
          </CardTitle>
          <CardDescription>
            Configure the Zoom/meeting link that students see on their dashboard
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="zoom-link" className="text-sm font-medium text-slate-700">
              Zoom / Meeting Link
            </Label>
            <Input
              id="zoom-link"
              value={zoomLink}
              onChange={(e) => setZoomLink(e.target.value)}
              placeholder="https://zoom.us/j/your-meeting-id"
              data-testid="zoom-link-input"
            />
            {zoomLink && (
              <a href={zoomLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                <ExternalLink className="w-3 h-3" /> Preview link
              </a>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="classroom-msg" className="text-sm font-medium text-slate-700">
              Message for Students
            </Label>
            <Textarea
              id="classroom-msg"
              value={classroomMessage}
              onChange={(e) => setClassroomMessage(e.target.value)}
              placeholder="Click the button below to join your classroom"
              rows={2}
              data-testid="classroom-message-input"
            />
          </div>
          <p className="text-sm text-slate-500 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {zoomLink ? "Students will see a \"Join Classroom\" button on their dashboard." : "No Zoom link set — students won't see the Join Classroom button."}
          </p>
        </CardContent>
      </Card>

      {/* Save All Button */}
      <div className="max-w-2xl">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-600 hover:bg-indigo-700 btn-active"
          data-testid="save-settings-btn"
        >
          {saving ? "Saving..." : "Save All Settings"}
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-slate-200 shadow-sm max-w-2xl bg-indigo-50 border-indigo-100">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-indigo-900">How deduction works</h3>
              <p className="text-sm text-indigo-700 mt-1">
                When you add or edit a class record, the system automatically calculates 
                the remaining classes based on these rules. The Remaining Classes shown 
                in each record reflects the balance after that specific class.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
